import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CONTACT } from "@/data/classes";
import Seo from "@/components/Seo";

export default function Contact() {
  const [params] = useSearchParams();
  const isGroupPrefill = params.get("inquiry") === "group";
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: isGroupPrefill ? "Group Training Quote Request" : "",
    message: "",
    is_group_inquiry: isGroupPrefill,
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (isGroupPrefill && !form.subject) {
      setForm((f) => ({ ...f, subject: "Group Training Quote Request", is_group_inquiry: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroupPrefill]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent!");
      setSent(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "", is_group_inquiry: false });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Seo
        title="Contact"
        path="/contact"
        description="Get in touch with Rhythm Check Training. Call 214-797-5539 or send us a message about a class, group booking, or workplace training in the DFW area."
      />
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="font-mono-label text-[#d90429] mb-4">Contact</div>
          <h1 className="font-display text-5xl md:text-6xl font-black text-[#0a0a0a] leading-none">
            Let&apos;s talk training.
          </h1>
          <p className="mt-6 text-lg text-gray-700 max-w-2xl">
            Questions about a class, a group booking, or which certification you need?
            We reply within one business day.
          </p>
        </div>
      </section>

      <section className="bg-[#f9fafb] py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <div className="font-mono-label text-[#d90429] mb-4">Direct</div>
              <ul className="space-y-4 text-gray-800">
                <li className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-[#d90429] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-[#0a0a0a]">Phone</div>
                    <a href={CONTACT.phoneHref} className="hover:text-[#d90429]" data-testid="contact-phone-link">
                      {CONTACT.phone}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-[#d90429] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-[#0a0a0a]">Email</div>
                    <a href={`mailto:${CONTACT.email}`} className="hover:text-[#d90429]">
                      {CONTACT.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#d90429] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-[#0a0a0a]">Service Area</div>
                    {CONTACT.area}
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-[#d90429] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-[#0a0a0a]">Hours</div>
                    {CONTACT.hours}
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-[#0a0a0a] text-white rounded-lg p-8">
              <h3 className="font-display text-2xl font-bold">Ready to book right now?</h3>
              <p className="mt-3 text-gray-300">Skip the form and grab a spot on our schedule.</p>
              <a
                href="/schedule"
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#d90429] px-5 py-3 text-sm font-bold text-white hover:bg-white hover:text-[#d90429] transition-colors"
                data-testid="contact-signup-cta"
              >
                Sign Up for a Class <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            {sent ? (
              <div className="bg-white border border-gray-200 rounded-lg p-10 text-center" data-testid="contact-success">
                <div className="font-mono-label text-[#d90429] mb-3">Sent</div>
                <h2 className="font-display text-3xl font-black text-[#0a0a0a]">
                  Message received.
                </h2>
                <p className="mt-4 text-gray-700">
                  Thanks for reaching out — we&apos;ll get back to you shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white border border-gray-200 rounded-lg p-8 space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Your name" name="name" value={form.name} onChange={handleChange} required />
                  <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} required />
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Phone (optional)" name="phone" value={form.phone} onChange={handleChange} />
                  <Field label="Subject" name="subject" value={form.subject} onChange={handleChange} required />
                </div>
                <div>
                  <label className="font-mono-label block mb-2" htmlFor="message">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={form.message}
                    onChange={handleChange}
                    required
                    data-testid="contact-input-message"
                    className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-[#d90429] focus:ring-2 focus:ring-[#d90429]/20"
                  />
                </div>
                <label className="flex items-center gap-3 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    name="is_group_inquiry"
                    checked={form.is_group_inquiry}
                    onChange={handleChange}
                    className="w-4 h-4"
                    data-testid="contact-group-checkbox"
                  />
                  This is a group / workplace training inquiry
                </label>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="w-full inline-flex justify-center items-center gap-2 rounded-md bg-[#d90429] px-8 py-4 text-base font-bold text-white hover:bg-black transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send Message"} <ArrowRight className="w-5 h-5" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, required }) {
  return (
    <div>
      <label className="font-mono-label block mb-2" htmlFor={name}>
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={`contact-input-${name.replace("_", "-")}`}
        className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-[#d90429] focus:ring-2 focus:ring-[#d90429]/20"
      />
    </div>
  );
}
